import { useState, useMemo, useRef } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { StepHandle } from '../context/TourContext';

/**
 * Hook for managing tour step registration and validation
 *
 * Handles:
 * - Tracking registered steps
 * - Computing expected step order
 * - Validating which expected steps are actually registered
 * - Registering and unregistering steps
 */
export const useTourStepRegistry = (tourName: string, stepOrder?: ReadonlyArray<string>) => {
  // Use state for steps map to ensure reactivity in context consumers
  const [steps, setSteps] = useState<Map<string, StepHandle>>(new Map());
  const stepsRef = useRef<Map<string, StepHandle>>(steps);
  const [registeredSteps, setRegisteredSteps] = useState<ReadonlySet<string>>(new Set());

  // Keep ref in sync with state for efficient lookups
  stepsRef.current = steps;

  // Expected step order: from stepOrder prop, or registration order if not provided
  const expectedStepNames = useMemo(() => {
    return stepOrder ?? Array.from(registeredSteps);
  }, [stepOrder, registeredSteps]);

  // Set of expected steps that are actually registered (for O(1) validation)
  const validExpectedSteps = useMemo(() => {
    const valid = new Set<string>();
    expectedStepNames.forEach(name => {
      if (registeredSteps.has(name)) {
        valid.add(name);
      }
    });
    return valid;
  }, [expectedStepNames, registeredSteps]);

  // Register step - add to steps map and registered set
  const registerStep = useEventCallback((stepName: string, tourNameFromStep: string, handle: StepHandle) => {
    // Only register if step belongs to this tour
    if (tourNameFromStep !== tourName) {
      return;
    }

    // Update steps map state to trigger reactivity
    setSteps(prev => {
      const next = new Map(prev);
      next.set(stepName, handle);
      return next;
    });

    // Add to registered steps set if not already present
    setRegisteredSteps(prev => {
      if (prev.has(stepName)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(stepName);
      return next;
    });
  });

  // Unregister step - remove from steps map and registered set
  const unregisterStep = useEventCallback((stepName: string, tourNameFromStep: string) => {
    if (tourNameFromStep !== tourName) {
      return;
    }

    // Update steps map state to trigger reactivity
    setSteps(prev => {
      const next = new Map(prev);
      next.delete(stepName);
      return next;
    });

    // Remove from registered steps set
    setRegisteredSteps(prev => {
      if (!prev.has(stepName)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(stepName);
      return next;
    });
  });

  return {
    stepsRef, // Keep ref for backward compatibility and efficient lookups
    steps, // State version for reactivity
    expectedStepNames,
    validExpectedSteps,
    registerStep,
    unregisterStep,
  };
};
